// src/components/ExerciseLog.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../App";
import {
  getExerciseEntriesByDate,
  getExerciseEntriesRange,
  addExerciseEntry,
  deleteExerciseEntry,
} from "../services/firebase";

const today = () => new Date().toISOString().slice(0, 10);
const nDaysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const emptyForm = () => ({ exercise_type: "", duration_minutes: "", calories_burned: "" });

export default function ExerciseLog() {
  const { user } = useApp();
  const [dateStr,     setDateStr]     = useState(today());
  const [entries,     setEntries]     = useState([]);
  const [weekEntries, setWeekEntries] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [form,        setForm]        = useState(emptyForm());

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: day }, { data: week }] = await Promise.all([
      getExerciseEntriesByDate(user.uid, dateStr),
      getExerciseEntriesRange(user.uid, nDaysAgo(6), today()),
    ]);
    setEntries(day || []);
    setWeekEntries(week || []);
    setLoading(false);
  }, [user, dateStr]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAdd = async () => {
    if (!form.exercise_type.trim())            { setError("Enter an exercise type."); return; }
    if (!form.duration_minutes || form.duration_minutes <= 0) { setError("Enter duration in minutes."); return; }
    if (!form.calories_burned  || form.calories_burned  < 0)  { setError("Enter estimated calories burned."); return; }
    setError(""); setSaving(true);

    await addExerciseEntry(user.uid, {
      date:             dateStr,
      exercise_type:    form.exercise_type.trim(),
      duration_minutes: parseInt(form.duration_minutes, 10),
      calories_burned:  parseFloat(form.calories_burned),
    });

    await load();
    setForm(emptyForm());
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await deleteExerciseEntry(user.uid, id);
    await load();
  };

  // Weekly summary
  const weekTotals = weekEntries.reduce(
    (acc, e) => ({
      sessions: acc.sessions + 1,
      minutes:  acc.minutes  + (e.duration_minutes || 0),
      calories: acc.calories + (e.calories_burned  || 0),
    }),
    { sessions: 0, minutes: 0, calories: 0 }
  );

  return (
    <div className="space-y-5 animate-slide-up">
      <div className="flex items-center justify-between">
        <h1 className="section-title">Exercise Log</h1>
        <input type="date" className="input w-auto text-xs" value={dateStr}
          onChange={(e) => setDateStr(e.target.value)} max={today()} />
      </div>

      {/* ── Important disclaimer ──────────────────────────────────────── */}
      <div className="flex items-start gap-3 bg-[#111820] border border-[#263347] rounded-xl px-4 py-3">
        <span className="text-xl flex-shrink-0">ℹ️</span>
        <div>
          <div className="text-sm font-semibold text-slate-200">Exercise is tracked for awareness only</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Exercise calories are <strong className="text-slate-400">NOT</strong> added to your food budget.
            Your calorie target stays fixed — this protects your deficit.
            Don't "eat back" exercise calories.
          </div>
        </div>
      </div>

      {/* ── Weekly summary ────────────────────────────────────────────── */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-200 mb-3">This Week (7 days)</div>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Sessions",   value: weekTotals.sessions, icon: "🏃" },
            { label: "Minutes",    value: weekTotals.minutes,  icon: "⏱️" },
            { label: "Cal Burned", value: Math.round(weekTotals.calories), icon: "🔥" },
          ].map(({ label, value, icon }) => (
            <div key={label} className="bg-[#111820] rounded-lg p-3 text-center">
              <div className="text-xl mb-1">{icon}</div>
              <div className="text-2xl font-bold text-slate-100"
                   style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {value}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Log form ─────────────────────────────────────────────────── */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-200 mb-4">Log Exercise</div>
        <div className="space-y-3">
          <div>
            <label className="label">Exercise Type</label>
            <input className="input" placeholder="e.g. Running, Weight Training, Cycling"
              value={form.exercise_type} onChange={(e) => setField("exercise_type", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Duration (min)</label>
              <input type="number" className="input" placeholder="45" min="1" max="300"
                value={form.duration_minutes} onChange={(e) => setField("duration_minutes", e.target.value)} />
            </div>
            <div>
              <label className="label">Est. Cal Burned</label>
              <input type="number" className="input" placeholder="300" min="0"
                value={form.calories_burned} onChange={(e) => setField("calories_burned", e.target.value)} />
            </div>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <button onClick={handleAdd} disabled={saving} className="btn-primary w-full">
            {saving ? "Logging…" : "Log Exercise"}
          </button>
        </div>
      </div>

      {/* ── Today's entries ───────────────────────────────────────────── */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-200 mb-3">
          Entries for {dateStr}
        </div>
        {loading ? (
          <div className="space-y-2">
            {[1,2].map((i) => <div key={i} className="h-14 bg-[#111820] rounded-lg animate-pulse" />)}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <span className="text-3xl block mb-2">🏋️</span>
            <p className="text-sm">No exercise logged for {dateStr}.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <div key={entry.id}
                className="flex items-center justify-between bg-[#111820] rounded-lg px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-200">{entry.exercise_type}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {entry.duration_minutes} min · {entry.calories_burned} cal burned
                  </div>
                </div>
                <button onClick={() => handleDelete(entry.id)}
                  className="text-slate-600 hover:text-red-400 transition-colors text-xs px-2 py-1 ml-3">
                  ✕
                </button>
              </div>
            ))}

            {/* Day totals */}
            <div className="flex justify-between px-4 py-2 bg-[#0d1117] rounded-lg mt-1">
              <span className="text-xs text-slate-500">
                {entries.length} session{entries.length !== 1 ? "s" : ""} ·{" "}
                {entries.reduce((s, e) => s + (e.duration_minutes || 0), 0)} min
              </span>
              <span className="text-xs text-slate-400 font-medium">
                🔥 {Math.round(entries.reduce((s, e) => s + (e.calories_burned || 0), 0))} cal burned (not added to food budget)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── All week entries ──────────────────────────────────────────── */}
      {weekEntries.length > 0 && (
        <div className="card">
          <div className="text-sm font-semibold text-slate-200 mb-3">7-Day Activity</div>
          <div className="space-y-2">
            {Object.entries(
              weekEntries.reduce((acc, e) => {
                if (!acc[e.date]) acc[e.date] = [];
                acc[e.date].push(e);
                return acc;
              }, {})
            )
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([date, dayEntries]) => (
                <div key={date} className="bg-[#111820] rounded-lg px-4 py-3">
                  <div className="text-xs text-slate-500 mb-1.5">{date}</div>
                  {dayEntries.map((e) => (
                    <div key={e.id} className="text-sm text-slate-300">
                      {e.exercise_type} — {e.duration_minutes}min / {e.calories_burned} cal
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
